import React from "react"
import { keepPreviousData } from "@tanstack/react-query"
import { useDebounce as useDebouncedValue } from "hooks/use-debounced-value"
import { useSearchSubscribers } from "sdk/command-palette/queries"
import { SUBPAGES } from "../pages"

export const PAGE_ID = "subscriber-search"
export const SHORTCUT = "f s"

const MIN_QUERY_LENGTH = SUBPAGES[PAGE_ID].minQueryLength
const DEBOUNCE_MS = 300

export const SubscriberItem = ({ subscriber }) =>
  subscriber.first_name ? (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 max-w-[50%] truncate font-medium">
        {subscriber.first_name}
      </span>
      <span className="min-w-0 flex-1 truncate font-normal text-gray-700">
        {subscriber.email_address}
      </span>
    </span>
  ) : (
    <span className="min-w-0 flex-1 truncate font-medium">
      {subscriber.email_address}
    </span>
  )

const SEARCH_ENTRY_COMMAND = {
  id: "search-subscribers",
  label: "Search Subscribers…",
  icon: "search",
  group: "Search",
  keywords: ["find", "subscriber", "lookup"],
  shortcut: SHORTCUT,
  onSelect: api => api.pushPage(PAGE_ID)
}

/**
 * Provider for subscriber search. At the root it offers the "Search
 * subscribers…" entry that opens the subpage; on that subpage it returns the
 * async, server-filtered results (selecting one navigates to the subscriber).
 * On any other subpage it stays out of the way.
 *
 * @type {import("../types").Provider}
 */
export function useSubscriberSearchProvider({ query, activePageId }) {
  const active = activePageId === PAGE_ID
  const trimmedQuery = query.trim()
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS)

  const { data, isFetching } = useSearchSubscribers({
    params: { query: debouncedQuery },
    enabled:
      active &&
      debouncedQuery === trimmedQuery &&
      debouncedQuery.length >= MIN_QUERY_LENGTH,
    placeholderData: keepPreviousData
  })

  if (!activePageId) {
    return { commands: [SEARCH_ENTRY_COMMAND], isLoading: false }
  }

  if (!active) return { commands: [], isLoading: false }

  const commands = (data?.subscribers ?? []).map(subscriber => ({
    id: `subscriber-${subscriber.id}`,
    label: <SubscriberItem subscriber={subscriber} />,
    icon: "one-person",
    group: "Subscribers",
    onSelect: api => api.navigate(`/subscribers/${subscriber.id}`)
  }))

  return { commands, isLoading: isFetching }
}

/**
 * @returns {import("../types").Shortcut[]}
 */
export function getSubscriberSearchShortcuts() {
  return [{ shortcut: SHORTCUT, onTrigger: api => api.pushPage(PAGE_ID) }]
}
